import { useState } from 'react';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import Tooltip from '@/componentsDS/Tooltip/Tooltip';
import { useFeature } from '@/hooks/use-feature/index';
import { featureConstant } from '@/constants/configuration/feature';
import { ReactComponent as IconInfo } from '@/assets/svgs/icon-info-filled.svg';

/**
 * Some schema fields (notably top-level sections) store their description as
 * a structured HTML blob like:
 *
 *   <root>
 *     <div><label>description</label>:<b>Project Details</b></div>
 *     <div><label>businessTerm</label>:<b>Project Details</b></div>
 *     <div><label>businessDefinition</label>:<b>...</b></div>
 *     ...
 *   </root>
 *
 * For those, prefer the `description` value, fall back to `businessTerm`.
 * Plain-text descriptions (most field-level descriptions) pass through unchanged.
 */
export const resolveDescription = (input) => {
    if (typeof input !== 'string' || !input) return input;
    if (!input.includes('<label>')) return input;

    const fields = {};
    const pattern = /<label>([^<]+)<\/label>[^<]*<b>([^<]*)<\/b>/g;
    let match;
    while ((match = pattern.exec(input)) !== null) {
        fields[match[1].trim()] = match[2].trim();
    }
    return fields.description || fields.businessTerm || '';
};

/**
 * Small grey info icon that shows the resolved description in a tooltip on hover.
 * Uses the DS Tooltip; renders nothing when there's no description to show.
 *
 * Each instance gets a unique tooltip id so descriptions containing apostrophes
 * or quotes don't break react-tooltip's internal attribute-selector lookup.
 */
const InfoIcon = ({ content, iconClassName = 'eformv2__info-icon' }) => {
    const isSubmittalsEnabled = useFeature(featureConstant.SUBMITTALS);
    const [id] = useState(() => uniqueId('eform-info-'));
    if (!isSubmittalsEnabled) return null;
    const text = resolveDescription(content);
    if (!text) return null;
    return (
        <Tooltip id={id} content={text} placement="top">
            <span
                className={iconClassName}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <IconInfo />
            </span>
        </Tooltip>
    );
};

InfoIcon.propTypes = {
    content: PropTypes.string,
    iconClassName: PropTypes.string,
};

export default InfoIcon;
